import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { Subject, Subscription } from 'rxjs';
import { IFileDocument, IFilePageAndWords, IFilePageDetails, TChangePageEvent } from 'src/modules/shared/models/interfaces/files.interface';
import { ISmartCanvasOptions } from 'src/modules/shared/models/interfaces/smart-canvas.interface';
import { EventEmitterService } from 'src/modules/shared/services/event-emitter.service';

@Component({
  selector: 'hcr-smart-canvas-pagination',
  templateUrl: './smart-canvas-pagination.component.html',
  styleUrls: ['./smart-canvas-pagination.component.sass'],
})
export class SmartCanvasPaginationComponent implements OnChanges {
  private _pagesChangeEventName = 'change-pages-to-show';
  @Input() selectedFilePagesAndWords!: IFilePageAndWords[];
  @Input() pagesToShow!: number[];
  @Input() smartCanvasOptions!: ISmartCanvasOptions;
  @Input() selectedFile: IFileDocument;
  @Input() selectedFileNumberOfPages: number;
  @Output() pagesToShowChange = new EventEmitter<number[]>();

  public pagesSelectedString = '1';

  public icons = {
    chevronRight: faChevronRight,
    chevronLeft: faChevronLeft,
  };

  public get isLast(): boolean {
    const lastPage: number = this.pagesToShow[this.pagesToShow.length - 1];
    return lastPage >= this.selectedFileNumberOfPages;
  }

  public get isFirst(): boolean {
    return this.pagesToShow[0] === 1;
  }

  private _changeEvent = true;

  public constructor(private _eventEmitterService: EventEmitterService) {}

  public nextPages(): void {
    const lastPage: number = this.pagesToShow[this.pagesToShow.length - 1];
    const nextPages: number[] = [];
    for (let i = lastPage + 1; i <= lastPage + this.pagesToShow.length; i++) {
      if (i <= this.selectedFileNumberOfPages) {
        nextPages.push(i);
      }
    }
    this.pagesToShow = nextPages;
    this._emitPagesToShowChangeEvent();
  }

  public previousPages(): void {
    const firstPage: number = this.pagesToShow[0];
    const previousPages: number[] = [];
    for (let i = firstPage - 1; i >= firstPage - this.pagesToShow.length; i--) {
      if (i > 0) {
        previousPages.push(i);
      }
    }
    this.pagesToShow = previousPages.reverse();
    this._emitPagesToShowChangeEvent();
  }

  /**
   * Parse string of range of pages to array of numbers like Google Chrome PDF print dialog
   * @param pagesSelectedString string of pages selected
   */
  public convertPagesStringToArray(): void {
    let pagesSelectedArray: number[] = [];
    let order = false;
    let pagesSelectedStringArray: string[] = this.pagesSelectedString.trim().split(',');

    if (pagesSelectedStringArray.length > 1) {
      pagesSelectedStringArray = pagesSelectedStringArray.sort((x, y) => {
        if (Number(x) > Number(y)) return 1;
        return -1;
      });
      pagesSelectedStringArray.forEach((page) => {
        pagesSelectedArray.push(parseInt(page));
      });
      order = true;
      pagesSelectedArray = [...new Set(pagesSelectedArray)];
    } else if (pagesSelectedStringArray.length === 1) {
      pagesSelectedStringArray.forEach((pageString: string) => {
        const pageRange: string[] = pageString.split('-');
        if (pageRange.length === 1 && pageRange[0]) {
          pagesSelectedArray.push(parseInt(pageRange[0], 10));
        } else {
          const startPage: number = parseInt(pageRange[0], 10);
          const endPage: number = parseInt(pageRange[1], 10);
          for (let i = startPage; i <= endPage; i++) {
            pagesSelectedArray.push(i);
          }
          pagesSelectedArray = [...new Set(pagesSelectedArray)];
        }
      });
    }

    if (pagesSelectedArray.length === 0 || pagesSelectedArray.filter((number) => number <= 0).length) {
      pagesSelectedArray = new Array(this.smartCanvasOptions.maxPagesToRead).fill(1).map((_, i) => i + 1);
      this._convertPagesArrayToString();
    }

    this._changeEvent = false;
    this.pagesToShow = pagesSelectedArray;
    if (order) {
      this._convertPagesArrayToString();
    }
    this._emitPagesToShowChangeEvent();
  }

  private _emitPagesToShowChangeEvent() {
    this.pagesToShowChange.emit(this.pagesToShow);
    this._eventEmitterService.get<TChangePageEvent>(this._pagesChangeEventName).emit({
      fileId: this.selectedFile.id,
      pagesToShow: this.pagesToShow
    });
  }

  private _convertPagesArrayToString(): void {
    let pagesSelectedString = '';
    this.pagesToShow.forEach((page: number) => {
      if (pagesSelectedString.length > 0) {
        pagesSelectedString += ',';
      }
      pagesSelectedString += page.toString();
    });
    this.pagesSelectedString = pagesSelectedString;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.pagesToShow?.currentValue) {
      if (this._changeEvent) {
        this._convertPagesArrayToString();
      } else {
        this._changeEvent = true;
      }
    }
  }
}
